import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { avatars, scenes } from '@/config/siteImages';
import { SITE_ORIGIN } from '@/config/siteUrl';

export default function AboutPage() {
  useLanguageFromPath();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'team' | 'mentors'>('team');

  useEffect(() => {
    const isEs = i18n.language === 'es';
    document.title = isEs
      ? 'Sobre Nosotros | 15+ Años Guiando Empresarios | Kailac'
      : 'About Kailac | 15+ Years Guiding Qualified Business Owners to Funding';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', isEs
      ? 'Kailac fue fundado en 2009 con la misión de hacer accesible la guía experta en financiamiento. Conoce nuestro equipo y descubre cómo 15+ años de experiencia moldean cada relación con clientes.'
      : 'Kailac was founded in 2009 with a mission to make expert business funding guidance accessible. Meet our team, learn our story, and discover how 15+ years of experience shapes every client relationship.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', isEs ? `${SITE_ORIGIN}/es/sobre-nosotros` : `${SITE_ORIGIN}/about`);
    let hreflangEn = document.querySelector('link[data-page="about"][hreflang="en"]') as HTMLLinkElement | null;
    let hreflangEs = document.querySelector('link[data-page="about"][hreflang="es"]') as HTMLLinkElement | null;
    if (!hreflangEn) { hreflangEn = document.createElement('link'); hreflangEn.setAttribute('rel', 'alternate'); hreflangEn.setAttribute('hreflang', 'en'); hreflangEn.setAttribute('data-page', 'about'); document.head.appendChild(hreflangEn); }
    if (!hreflangEs) { hreflangEs = document.createElement('link'); hreflangEs.setAttribute('rel', 'alternate'); hreflangEs.setAttribute('hreflang', 'es'); hreflangEs.setAttribute('data-page', 'about'); document.head.appendChild(hreflangEs); }
    hreflangEn.setAttribute('href', `${SITE_ORIGIN}/about`);
    hreflangEs.setAttribute('href', `${SITE_ORIGIN}/es/sobre-nosotros`);
  }, [i18n.language]);

  const team = [
    {
      nameKey: 'about_team_1_name',
      roleKey: 'about_team_1_role',
      expKey: 'about_team_1_exp',
      bioKey: 'about_team_1_bio',
      avatar: avatars.small.aboutTeam1,
      badgeKeys: ['about_team_badge_credit', 'about_team_badge_strategy', 'about_team_badge_advisory'],
      featured: true,
    },
    {
      nameKey: 'about_team_2_name',
      roleKey: 'about_team_2_role',
      expKey: 'about_team_2_exp',
      bioKey: 'about_team_2_bio',
      avatar: avatars.small.aboutTeam2,
      badgeKeys: ['about_team_badge_strategy2', 'about_team_badge_bilingual', 'about_team_badge_success'],
      featured: false,
    },
    {
      nameKey: 'about_team_3_name',
      roleKey: 'about_team_3_role',
      expKey: 'about_team_3_exp',
      bioKey: 'about_team_3_bio',
      avatar: avatars.small.aboutTeam3,
      badgeKeys: ['about_team_badge_programs', 'about_team_badge_smb', 'about_team_badge_capital'],
      featured: false,
    },
  ];

  const mentors = [
    {
      nameKey: 'about_mentor_1_name',
      roleKey: 'about_mentor_1_role',
      expKey: 'about_mentor_1_exp',
      bioKey: 'about_mentor_1_bio',
      avatar: avatars.small.aboutMentor1b,
      badgeKeys: ['about_mentor_badge_lending', 'about_mentor_badge_smb', 'about_mentor_badge_risk'],
    },
    {
      nameKey: 'about_mentor_2_name',
      roleKey: 'about_mentor_2_role',
      expKey: 'about_mentor_2_exp',
      bioKey: 'about_mentor_2_bio',
      avatar: avatars.small.aboutMentor2b,
      badgeKeys: ['about_mentor_badge_strategy', 'about_mentor_badge_latino', 'about_mentor_badge_bilingual'],
    },
  ];

  const milestones = [
    { yearKey: 'about_milestone_1_year', eventKey: 'about_milestone_1_event', icon: 'ri-flag-line', color: 'bg-[#4F2D63]' },
    { yearKey: 'about_milestone_2_year', eventKey: 'about_milestone_2_event', icon: 'ri-translate-2', color: 'bg-[#4F2D63]' },
    { yearKey: 'about_milestone_3_year', eventKey: 'about_milestone_3_event', icon: 'ri-funds-line', color: 'bg-amber-600' },
    { yearKey: 'about_milestone_4_year', eventKey: 'about_milestone_4_event', icon: 'ri-service-line', color: 'bg-[#0F766E]' },
    { yearKey: 'about_milestone_5_year', eventKey: 'about_milestone_5_event', icon: 'ri-group-line', color: 'bg-amber-600' },
    { yearKey: 'about_milestone_6_year', eventKey: 'about_milestone_6_event', icon: 'ri-trophy-line', color: 'bg-[#4F2D63]' },
  ];

  const values = [
    { icon: 'ri-eye-line', titleKey: 'why_val_1_title', descKey: 'why_val_1_desc', color: 'bg-[#F8F3FB]', iconColor: 'text-[#4F2D63]' },
    { icon: 'ri-user-heart-line', titleKey: 'why_val_2_title', descKey: 'why_val_2_desc', color: 'bg-[#F0FDF9]', iconColor: 'text-[#0F766E]' },
    { icon: 'ri-shield-check-line', titleKey: 'why_val_3_title', descKey: 'why_val_3_desc', color: 'bg-amber-50', iconColor: 'text-amber-700' },
    { icon: 'ri-translate-2', titleKey: 'why_val_4_title', descKey: 'why_val_4_desc', color: 'bg-[#F8F3FB]', iconColor: 'text-[#4F2D63]' },
  ];

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* ── Hero — full width, editorial 2-col ── */}
      <section className="bg-[#F8F3FB] pt-28 pb-0 px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-end">
            {/* Left: text */}
            <div className="pb-16 lg:pb-20 pr-0 lg:pr-12">
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('about_eyebrow')}</p>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {t('about_hero_title_1')}<br />
                <span className="text-[#4F2D63]">{t('about_hero_title_2')}</span>
              </h1>
              <p className="text-base text-gray-500 leading-relaxed mb-8 max-w-lg">
                {t('about_hero_subtitle')}
              </p>
              {/* Mini stats */}
              <div className="flex flex-wrap gap-5">
                {[
                  { val: '15+', label: i18n.language === 'es' ? 'Años de experiencia' : 'Years of experience' },
                  { val: '$47M+', label: i18n.language === 'es' ? 'Guiados' : 'Guided' },
                  { val: '2,400+', label: i18n.language === 'es' ? 'Negocios' : 'Businesses' },
                ].map(({ val, label }) => (
                  <div key={val} className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">{val}</span>
                    <span className="text-sm text-gray-500">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: image mosaic */}
            <div className="hidden lg:grid grid-cols-2 gap-3 pb-0 self-end">
              <div className="rounded-t-2xl overflow-hidden h-72 mt-8">
                <img
                  src={scenes.aboutHero1}
                  alt="Kailac team meeting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-t-2xl overflow-hidden h-60 mt-16">
                <img
                  src={scenes.aboutHero2}
                  alt="Kailac advisory session"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats bar — dark ── */}
      <AnimatedSection>
        <section className="bg-[#1F0D35] py-10 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { val: '$47M+', desc: t('stat_1_label') },
              { val: '2,400+', desc: t('stat_2_label') },
              { val: '15+', desc: t('stat_3_label') },
              { val: '50+', desc: t('stat_4_label') },
            ].map(({ val, desc }) => (
              <div key={val}>
                <p className="text-3xl font-bold text-white mb-1">{val}</p>
                <p className="text-xs text-white/50 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>
      </AnimatedSection>

      {/* ── Mission & Story ── */}
      <AnimatedSection>
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div>
                <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">{t('about_mission_eyebrow')}</p>
                <h2 className="text-4xl font-bold text-gray-900 mb-8 leading-tight">{t('about_mission_title')}</h2>
                <div className="space-y-5">
                  {[t('about_mission_body_1'), t('about_mission_body_2'), t('about_mission_body_3')].map((body, i) => (
                    <p key={i} className="text-base text-gray-600 leading-relaxed">{body}</p>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#4F2D63] text-white font-semibold text-sm hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap">
                    {t('about_cta_primary')} <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link to="/how-it-works" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-[#E9D9F2] text-gray-700 font-semibold text-sm hover:border-[#4F2D63] transition-colors cursor-pointer whitespace-nowrap">
                    {t('about_cta_secondary')}
                  </Link>
                </div>
              </div>

              {/* Pull quote + images */}
              <div className="space-y-5">
                {/* Pull quote */}
                <blockquote className="bg-[#F8F3FB] rounded-2xl p-7 border-l-4 border-[#4F2D63]">
                  <i className="ri-double-quotes-l text-3xl text-[#DBB6EE] mb-3 block"></i>
                  <p className="text-lg font-semibold text-gray-900 leading-relaxed italic">
                    {i18n.language === 'es'
                      ? 'Los dueños de negocios calificados merecen acceso a las mismas herramientas que las grandes empresas dan por sentadas.'
                      : 'Qualified business owners deserve access to the same tools and expertise that big companies take for granted.'}
                  </p>
                  <p className="text-sm text-[#4F2D63] font-semibold mt-4">{t('about_team_1_name')}, {t('about_team_1_role')}</p>
                </blockquote>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl overflow-hidden h-40">
                    <img
                      src={scenes.aboutMission1}
                      alt="Advisory work"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="rounded-xl overflow-hidden h-40">
                    <img
                      src={scenes.aboutMission2}
                      alt="Client success"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* ── Values ── */}
      <AnimatedSection>
        <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">
                {i18n.language === 'es' ? 'Lo que nos define' : 'What Defines Us'}
              </p>
              <h2 className="text-3xl font-bold text-gray-900">{t('why_values_title')}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {values.map(({ icon, titleKey, descKey, color, iconColor }) => (
                <div key={titleKey} className="bg-white rounded-2xl p-6 border border-[#E9D9F2] flex flex-col">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${color} mb-4`}>
                    <i className={`${icon} ${iconColor} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2">{t(titleKey)}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed flex-1">{t(descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* ── Journey / Timeline ── */}
      <AnimatedSection>
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">
                {i18n.language === 'es' ? 'Nuestra historia' : 'Our History'}
              </p>
              <h2 className="text-3xl font-bold text-gray-900">{t('about_journey_title')}</h2>
            </div>

            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-1/2 -translate-x-px top-0 bottom-0 w-0.5 bg-[#E9D9F2] hidden lg:block" />

              <div className="space-y-8">
                {milestones.map(({ yearKey, eventKey, icon, color }, idx) => {
                  const isLeft = idx % 2 === 0;
                  return (
                    <div key={yearKey} className="relative flex items-center gap-4 lg:gap-0">
                      {/* Left content (desktop) */}
                      <div className={`hidden lg:flex lg:w-[calc(50%-2rem)] ${isLeft ? 'justify-end pr-8' : 'justify-start pl-8 order-last'}`}>
                        <div className={`bg-white border border-[#E9D9F2] rounded-xl p-5 max-w-sm ${isLeft ? 'text-right' : 'text-left'}`}>
                          <p className={`text-xs font-bold text-[#4F2D63] uppercase tracking-widest mb-2`}>{t(yearKey)}</p>
                          <p className="text-sm text-gray-700 leading-relaxed">{t(eventKey)}</p>
                        </div>
                      </div>

                      {/* Center dot */}
                      <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 z-10">
                        <div className={`w-12 h-12 flex items-center justify-center rounded-full ${color} text-white`}>
                          <i className={`${icon} text-base`}></i>
                        </div>
                      </div>

                      {/* Right spacer (desktop) */}
                      <div className={`hidden lg:block lg:w-[calc(50%-2rem)] ${!isLeft ? '' : 'order-last'}`} />

                      {/* Mobile layout */}
                      <div className={`flex lg:hidden items-start gap-4 w-full`}>
                        <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${color} text-white flex-shrink-0`}>
                          <i className={`${icon} text-base`}></i>
                        </div>
                        <div className="flex-1 bg-white border border-[#E9D9F2] rounded-xl p-4">
                          <p className="text-xs font-bold text-[#4F2D63] uppercase tracking-widest mb-1">{t(yearKey)}</p>
                          <p className="text-sm text-gray-700 leading-relaxed">{t(eventKey)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* ── Team & Mentors — tab toggle ── */}
      <AnimatedSection>
        <section className="py-20 bg-[#F8F3FB] px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {/* Tab header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
              <div>
                <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-2">
                  {activeTab === 'team' ? t('about_team_eyebrow') : t('about_mentors_eyebrow')}
                </p>
                <h2 className="text-3xl font-bold text-gray-900">
                  {activeTab === 'team' ? t('about_team_title') : t('about_mentors_title')}
                </h2>
              </div>
              {/* Tab pills */}
              <div className="flex bg-white border border-[#E9D9F2] rounded-xl p-1 gap-1 self-start sm:self-auto">
                <button
                  onClick={() => setActiveTab('team')}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'team' ? 'bg-[#4F2D63] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {t('about_team_eyebrow')}
                </button>
                <button
                  onClick={() => setActiveTab('mentors')}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'mentors' ? 'bg-[#4F2D63] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {t('about_mentors_eyebrow')}
                </button>
              </div>
            </div>

            {/* Team tab */}
            {activeTab === 'team' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {team.map(({ nameKey, roleKey, expKey, bioKey, avatar, badgeKeys, featured }) => (
                  <div key={nameKey} className={`rounded-2xl overflow-hidden border ${featured ? 'border-[#DBB6EE] bg-white' : 'border-[#E9D9F2] bg-white'} flex flex-col`}>
                    {/* Photo */}
                    <div className="w-full h-52 overflow-hidden">
                      <img src={avatar} alt={t(nameKey)} className="w-full h-full object-cover object-top" />
                    </div>
                    {/* Info */}
                    <div className="p-6 flex flex-col flex-1">
                      {featured && (
                        <span className="text-xs bg-[#F8F3FB] text-[#4F2D63] font-semibold px-2.5 py-1 rounded-full self-start mb-3">
                          {i18n.language === 'es' ? 'Fundador' : 'Founder'}
                        </span>
                      )}
                      <h3 className="text-lg font-bold text-gray-900">{t(nameKey)}</h3>
                      <p className="text-sm text-[#4F2D63] font-medium">{t(roleKey)}</p>
                      <p className="text-xs text-gray-400 mt-0.5 mb-3">{t(expKey)}</p>
                      <p className="text-sm text-gray-500 leading-relaxed mb-4 flex-1">{t(bioKey)}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {badgeKeys.map((bk) => (
                          <span key={bk} className="text-xs bg-[#F8F3FB] text-[#4F2D63] px-2.5 py-1 rounded-full">{t(bk)}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Mentors tab */}
            {activeTab === 'mentors' && (
              <div>
                <p className="text-sm text-gray-500 mb-8 max-w-2xl">{t('about_mentors_subtitle')}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {mentors.map(({ nameKey, roleKey, expKey, bioKey, avatar, badgeKeys }) => (
                    <div key={nameKey} className="bg-white border border-[#E9D9F2] rounded-2xl overflow-hidden flex flex-col sm:flex-row">
                      <div className="w-full sm:w-36 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                        <img src={avatar} alt={t(nameKey)} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full self-start mb-3">
                          <i className="ri-award-line text-xs"></i>
                          {t(expKey)}
                        </div>
                        <h3 className="text-base font-bold text-gray-900">{t(nameKey)}</h3>
                        <p className="text-sm text-[#4F2D63] font-medium mb-3">{t(roleKey)}</p>
                        <p className="text-sm text-gray-500 leading-relaxed mb-4 flex-1">{t(bioKey)}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {badgeKeys.map((bk) => (
                            <span key={bk} className="text-xs bg-[#F8F3FB] text-[#4F2D63] px-2 py-0.5 rounded-full border border-[#E9D9F2]">{t(bk)}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </AnimatedSection>

      {/* ── Culture strip ── */}
      <AnimatedSection>
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl overflow-hidden h-56">
                <img
                  src={scenes.aboutCulture}
                  alt="Kailac culture"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="rounded-2xl bg-[#4F2D63] p-8 flex flex-col justify-between h-56">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#3E234F]">
                  <i className="ri-heart-line text-[#DBB6EE] text-xl"></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-white mb-2">
                    {i18n.language === 'es' ? 'Comunidad Latina' : 'Latino Community'}
                  </p>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {i18n.language === 'es'
                      ? 'Soporte bilingüe completo — el mismo nivel de expertise, en tu idioma.'
                      : 'Full bilingual support — same expert level, in your language.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* ── CTA ── */}
      <AnimatedSection>
        <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('about_cta_title')}</h2>
            <p className="text-gray-500 mb-2">{t('about_cta_subtitle')}</p>
            <p className="text-xs text-gray-400 italic mb-8">
              {i18n.language === 'es' ? 'Sin compromiso. Sin impacto al crédito.' : 'No commitment. No credit impact at application stage.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#4F2D63] text-white font-semibold hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap">
                {t('about_cta_primary')} <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to="/how-it-works" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                {t('about_cta_secondary')}
              </Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <Footer />
    </div>
  );
}
